import React, { useState, useEffect, useCallback } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { DatePicker, Button, Dropdown, DropdownTrigger, DropdownContent, DropdownItem } from 'equal-ds-ui';
import { smartRouterAPI } from '../../../services/smart-router-api.service';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import type { SmartRouterDateRange } from '../../../types/smart-router';

// Format a Date using its LOCAL calendar fields. Using toISOString() would convert
// to UTC and roll IST (UTC+5:30) dates back by a day (picking the 14th sends the
// 13th). Mirrors the reads that parse `${date}T00:00:00` as local midnight.
const toYMD = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function getDefaultDateRange(): SmartRouterDateRange {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    startDate: toYMD(startOfMonth),
    endDate: toYMD(now),
  };
}
import type {
  FIUEntry,
  TrafficSplitResponse,
  AADistributionResponse,
  ConversionResponse,
  AAConversionOverallResponse,
  AAConversionByFIPResponse,
} from '../../../types/smart-router';
import { SmartRouterKpiCards } from './SmartRouterKpiCards';
import { TrafficDistributionSection } from './TrafficDistributionSection';
import { ConsentConversionGauges } from './ConsentConversionGauges';
import { AAConversionSection } from './AAConversionSection';
import { FipPerformanceTable } from './FipPerformanceTable';

export const SmartRouterDashboardPage: React.FC = () => {
  const [fiuList, setFiuList] = useState<FIUEntry[]>([]);
  const [selectedFiu, setSelectedFiu] = useState<string>('');
  const [dateRange, setDateRange] = useState<SmartRouterDateRange>(getDefaultDateRange());

  const [trafficSplit, setTrafficSplit] = useState<TrafficSplitResponse | null>(null);
  const [aaDistribution, setAADistribution] = useState<AADistributionResponse | null>(null);
  const [conversion, setConversion] = useState<ConversionResponse | null>(null);
  const [aaConversionOverall, setAAConversionOverall] = useState<AAConversionOverallResponse | null>(null);
  const [aaConversionByFIP, setAAConversionByFIP] = useState<AAConversionByFIPResponse | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [aaConversionLevel, setAAConversionLevel] = useState<'consent' | 'user'>('consent');
  const [kpiLevel, setKpiLevel] = useState<'consent' | 'user'>('consent');
  const { errorMessage, handleError, clearError } = useErrorHandler();

  const [fiuLoaded, setFiuLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await smartRouterAPI.getFiuList();
        setFiuList(resp.fius);
        if (resp.fius.length > 0) {
          const firstFiu = resp.fius[0];
          setSelectedFiu(firstFiu.fiu_id);
          const latest = new Date(firstFiu.latest_date);
          const startOfMonth = new Date(latest.getFullYear(), latest.getMonth(), 1);
          setDateRange({
            startDate: toYMD(startOfMonth),
            endDate: toYMD(latest),
          });
        }
      } catch (error) {
        handleError(error, { component: 'SmartRouterDashboardPage', action: 'loadFiuList' });
      } finally {
        setFiuLoaded(true);
      }
    })();
  }, []);

  const fetchDashboardData = useCallback(async () => {
    if (!selectedFiu) return;
    const { startDate, endDate } = dateRange;
    try {
      setIsLoading(true);
      clearError();
      const [split, dist, conv] = await Promise.all([
        smartRouterAPI.getTrafficSplit(selectedFiu, startDate, endDate),
        smartRouterAPI.getAADistribution(selectedFiu, startDate, endDate),
        smartRouterAPI.getConversion(selectedFiu, startDate, endDate),
      ]);
      setTrafficSplit(split);
      setAADistribution(dist);
      setConversion(conv);
      setLastRefreshed(new Date());
    } catch (error) {
      handleError(error, { component: 'SmartRouterDashboardPage', action: 'fetchDashboardData' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedFiu, dateRange]);

  const fetchAAConversion = useCallback(async () => {
    if (!selectedFiu) return;
    const { startDate, endDate } = dateRange;
    try {
      const [aaOverall, aaFip] = await Promise.all([
        smartRouterAPI.getAAConversionOverall(selectedFiu, startDate, endDate, aaConversionLevel),
        smartRouterAPI.getAAConversionByFIP(selectedFiu, startDate, endDate, aaConversionLevel),
      ]);
      setAAConversionOverall(aaOverall);
      setAAConversionByFIP(aaFip);
    } catch (error) {
      handleError(error, { component: 'SmartRouterDashboardPage', action: 'fetchAAConversion' });
    }
  }, [selectedFiu, dateRange, aaConversionLevel]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);
  useEffect(() => { if (selectedFiu) fetchAAConversion(); }, [fetchAAConversion]);

  const handleFiuSelect = (fiuId: string) => {
    const fiu = fiuList.find(f => f.fiu_id === fiuId);
    setSelectedFiu(fiuId);
    if (fiu) {
      const latest = new Date(fiu.latest_date);
      const startOfMonth = new Date(latest.getFullYear(), latest.getMonth(), 1);
      setDateRange({
        startDate: toYMD(startOfMonth),
        endDate: toYMD(latest),
      });
    }
  };

  const hasData = trafficSplit !== null;

  return (
    <div className="h-full flex flex-col p-4 space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-text-primary tracking-wide">
          Smart Router Dashboard
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Monitor consent routing performance, traffic distribution, and conversion rates across Account Aggregators.
        </p>
      </div>

      {/* Controls Bar */}
      <div className="bg-white border border-border-default rounded-xl p-4 shadow-md">
        <div className="flex items-center gap-4">
          {/* FIU Dropdown */}
          <div className="min-w-[180px]">
            <label className="block text-xs font-normal text-text-tertiary tracking-wide mb-1">FIU</label>
            <Dropdown>
              <DropdownTrigger variant="default" className="w-full">
                <span className={`flex-1 text-left ${!selectedFiu ? 'text-text-secondary' : ''}`}>
                  {selectedFiu || 'Select FIU'}
                </span>
              </DropdownTrigger>
              <DropdownContent>
                {fiuList.map(fiu => (
                  <DropdownItem key={fiu.fiu_id} onSelect={() => handleFiuSelect(fiu.fiu_id)}>
                    {fiu.fiu_id}
                  </DropdownItem>
                ))}
              </DropdownContent>
            </Dropdown>
          </div>

          {/* Date Range */}
          <div className="min-w-[200px]">
            <label className="block text-xs font-normal text-text-tertiary tracking-wide mb-1">DATE RANGE</label>
            <DatePicker
              selectedRange={{
                startDate: dateRange.startDate ? new Date(dateRange.startDate + 'T00:00:00') : null,
                endDate: dateRange.endDate ? new Date(dateRange.endDate + 'T00:00:00') : null,
                startTime: null,
                endTime: null,
              }}
              rangeMode={true}
              dateOnly={true}
              closeOnSelect={true}
              maxDate={new Date()}
              onRangeChange={(range) => {
                if (range.startDate && range.endDate) {
                  setDateRange({
                    startDate: toYMD(range.startDate),
                    endDate: toYMD(range.endDate),
                  });
                }
              }}
              placeholder="Select date range"
              dateFormat="medium"
              triggerClassName="w-full"
            />
          </div>

          <div className="flex-1" />

          {/* Actions */}
          <div className="flex items-center gap-3 self-end">
            <span className="text-xs text-text-tertiary">
              Updated: {lastRefreshed.toLocaleTimeString('en-IN')}
            </span>
            <Button variant="secondary" size="md" disabled>
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="primary" size="md" onClick={fetchDashboardData} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Error */}
      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center justify-between">
          <span>{errorMessage}</span>
          <button onClick={clearError} className="text-red-500 hover:text-red-700 ml-2">✕</button>
        </div>
      )}

      {/* Loading */}
      {!fiuLoaded && (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
            <span className="text-text-secondary">Loading dashboard data...</span>
          </div>
        </div>
      )}

      {/* Empty state — FIU list loaded but empty or no FIU configured */}
      {fiuLoaded && !selectedFiu && !errorMessage && (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="text-text-secondary text-sm">No FIU data available. Ensure <code className="text-xs bg-background-secondary px-1.5 py-0.5 rounded">dashboardFiuId</code> is configured in <code className="text-xs bg-background-secondary px-1.5 py-0.5 rounded">smart_router_config</code>.</span>
          </div>
        </div>
      )}

      {/* Dashboard loading after FIU selected */}
      {fiuLoaded && selectedFiu && isLoading && !hasData && (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
            <span className="text-text-secondary">Loading dashboard data...</span>
          </div>
        </div>
      )}

      {/* Dashboard Content */}
      {hasData && (
        <div className="space-y-4">
          {trafficSplit && conversion && (
            <SmartRouterKpiCards trafficSplit={trafficSplit} conversion={conversion} level={kpiLevel} onLevelChange={setKpiLevel} />
          )}
          {aaDistribution && <TrafficDistributionSection data={aaDistribution} />}
          {conversion && <ConsentConversionGauges data={conversion} />}
          {aaConversionOverall && (
            <AAConversionSection data={aaConversionOverall} level={aaConversionLevel} onLevelChange={setAAConversionLevel} />
          )}
          {aaConversionByFIP && (
            <FipPerformanceTable data={aaConversionByFIP} level={aaConversionLevel} />
          )}
        </div>
      )}
    </div>
  );
};
